import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FiUpload, FiX, FiPlus } from "react-icons/fi";
import { ChromePicker } from "react-color";

//Components
import MenuList from "components/dropdown/MenuList";

//Action
import { createNewProductByAdmin } from "store/features/product/productActions";
import { fetchCategories } from "store/features/category/categoryActions";

//Selectors
import { selectCategories } from "store/features/category/categorySelectors";
import { showSuccessToast } from "utils/toastNotification";

const CreateProductByVendor = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  //category data
  const categories = useSelector(selectCategories) || [];
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const initialFormData = {
    name: "",
    description: "",
    price: "",
    discountPrice: "",
    stock: "",
    categories: "",
    colors: "",
    sizes: "",
    gender: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [images, setImages] = useState({
    mainImage: null,
    gallery: [],
  });
  const [previews, setPreviews] = useState({
    mainImage: null,
    gallery: [],
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorList, setColorList] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;

    if (name === "mainImage") {
      setImages((prev) => ({ ...prev, [name]: files[0] }));
      setPreviews((prev) => ({
        ...prev,
        [name]: files[0] ? URL.createObjectURL(files[0]) : null,
      }));
    } else if (name === "gallery") {
      setImages((prev) => ({ ...prev, [name]: Array.from(files) }));
      setPreviews((prev) => ({
        ...prev,
        [name]: Array.from(files).map((file) => URL.createObjectURL(file)),
      }));
    }
  };

  const removeGalleryImage = (index) => {
    const newGallery = [...images.gallery];
    newGallery.splice(index, 1);
    setImages((prev) => ({ ...prev, gallery: newGallery }));

    const newPreviews = [...previews.gallery];
    newPreviews.splice(index, 1);
    setPreviews((prev) => ({ ...prev, gallery: newPreviews }));
  };

  const handleColorChange = (color) => {
    const hexColor = color.hex;
    if (!colorList.includes(hexColor)) {
      setColorList([...colorList, hexColor]);
      setFormData((prev) => ({
        ...prev,
        colors: [...colorList, hexColor].join(", "),
      }));
    }
  };

  const removeColor = (colorToRemove) => {
    const updatedColors = colorList.filter((color) => color !== colorToRemove);
    setColorList(updatedColors);
    setFormData((prev) => ({
      ...prev,
      colors: updatedColors.join(", "),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      data.append(key, formData[key]);
    });

    if (images.mainImage) data.append("mainImage", images.mainImage);
    images.gallery.forEach((file) => data.append("gallery", file));

    dispatch(createNewProductByAdmin(data));
    showSuccessToast("Produvt Added Successfully.");
    setFormData(initialFormData);
    navigate("/admin/products");
  };

  return (
    <div className="max-h-screen w-full px-4 py-8">
      <div className="mx-auto ">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              Add New Product
            </h1>
            <p className="text-gray-600">
              Fill in the details to create a new product listing
            </p>
          </div>
          <button
            onClick={() => navigate("/admin/products")}
            className="border-transparent inline-flex items-center rounded-md border bg-indigo-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            Back
          </button>
        </div>

        <div className="rounded-xl bg-white p-8 shadow-sm">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              {/* Left Column */}
              <div>
                <div className="mb-6">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="e.g., Premium Cotton T-Shirt"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    rows="5"
                    placeholder="Detailed product description..."
                    required
                  />
                </div>

                <div className="mb-6">
                  <MenuList
                    name="categories"
                    options={categories.filter(
                      (cat) => cat.status === "active"
                    )}
                    value={formData.categories}
                    onChange={handleChange}
                    label="Select Category"
                    required
                    idKey="_id"
                    displayKey="name"
                  />
                </div>
                <div className="mb-4">
                  <label className="mb-2 block font-medium text-gray-700">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="" disabled>
                      Select Gender
                    </option>
                    <option value="men">Men</option>
                    <option value="women">Women</option>
                    <option value="unisex">Unisex</option>
                    <option value="kids">Kids</option>
                  </select>
                </div>
              </div>

              {/* Right Column */}
              <div>
                <div className="mb-6 grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Price (₹) *
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500">₹</span>
                      </div>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        className="block w-full rounded-md border border-gray-300 py-2 pl-7 pr-3 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Discount Price (₹)
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500">₹</span>
                      </div>
                      <input
                        type="number"
                        name="discountPrice"
                        value={formData.discountPrice}
                        onChange={handleChange}
                        className="block w-full rounded-md border border-gray-300 py-2 pl-7 pr-3 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Stock Quantity *
                  </label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleChange}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    min="0"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Colors *
                  </label>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {colorList.map((color, index) => (
                        <div
                          key={index}
                          className="flex items-center rounded-md border border-gray-200 px-2 py-1"
                        >
                          <div
                            className="mr-2 h-4 w-4 rounded-full border border-gray-300"
                            style={{ backgroundColor: color }}
                          />
                          <span className="text-xs">{color}</span>
                          <button
                            type="button"
                            onClick={() => removeColor(color)}
                            className="ml-2 text-gray-400 hover:text-red-500"
                          >
                            <FiX className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowColorPicker(!showColorPicker)}
                        className="flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm hover:bg-gray-50"
                      >
                        <FiPlus className="mr-1 h-4 w-4" />
                        Add Color
                      </button>
                      {showColorPicker && (
                        <div className="absolute z-10 mt-2">
                          <ChromePicker
                            color="#ffffff"
                            onChangeComplete={handleColorChange}
                          />
                          <button
                            type="button"
                            onClick={() => setShowColorPicker(false)}
                            className="mt-2 rounded-md bg-indigo-600 px-3 py-1 text-sm text-white"
                          >
                            Done
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Sizes *
                  </label>
                  <input
                    type="text"
                    name="sizes"
                    value={formData.sizes}
                    onChange={handleChange}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="e.g., S, M, L, XL"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Separate sizes with commas
                  </p>
                </div>
              </div>
            </div>

            {/* Image Uploads */}
            <div className="mt-8 border-t border-gray-200 pt-8">
              <h2 className="mb-6 text-lg font-medium text-gray-900">
                Product Images
              </h2>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="mb-3 block text-sm font-medium text-gray-700">
                    Main Image *
                  </label>
                  <div className="relative rounded-lg border-2 border-dashed border-gray-300 p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                    {previews.mainImage ? (
                      <div className="relative h-full w-full">
                        <img
                          src={previews.mainImage}
                          alt="Main preview"
                          className="mx-auto h-64 w-full object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImages((prev) => ({ ...prev, mainImage: null }));
                            setPreviews((prev) => ({
                              ...prev,
                              mainImage: null,
                            }));
                          }}
                          className="absolute right-2 top-2 rounded-full bg-white p-1 shadow-md hover:bg-gray-100"
                        >
                          <FiX className="h-5 w-5 text-red-600" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex h-64 flex-col items-center justify-center space-y-3">
                        <FiUpload className="h-12 w-12 text-gray-400" />
                        <p className="text-sm text-gray-600">
                          Upload main product image
                        </p>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, JPEG up to 5MB
                        </p>
                        <input
                          type="file"
                          name="mainImage"
                          onChange={handleFileChange}
                          className="hidden"
                          id="mainImage"
                          accept="image/*"
                          required
                        />
                        <label
                          htmlFor="mainImage"
                          className="border-transparent inline-flex items-center rounded-md border bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                          Select Image
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="mb-3 block text-sm font-medium text-gray-700">
                    Gallery Images
                  </label>
                  <div className="rounded-lg border-2 border-dashed border-gray-300 p-6 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                    {previews.gallery.length > 0 ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-3">
                          {previews.gallery.map((preview, index) => (
                            <div key={index} className="relative">
                              <img
                                src={preview}
                                alt={`Gallery preview ${index}`}
                                className="h-24 w-full rounded object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removeGalleryImage(index)}
                                className="absolute right-1 top-1 rounded-full bg-white p-1 shadow-md hover:bg-gray-100"
                              >
                                <FiX className="h-4 w-4 text-red-600" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <input
                          type="file"
                          name="gallery"
                          onChange={handleFileChange}
                          className="hidden"
                          id="gallery"
                          accept="image/*"
                          multiple
                        />
                        <label
                          htmlFor="gallery"
                          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                          <FiPlus className="mr-2 h-4 w-4" />
                          Add More Images
                        </label>
                      </div>
                    ) : (
                      <div className="flex h-64 flex-col items-center justify-center space-y-3">
                        <FiUpload className="h-12 w-12 text-gray-400" />
                        <p className="text-sm text-gray-600">
                          Upload additional product images
                        </p>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, JPEG up to 5MB each
                        </p>
                        <input
                          type="file"
                          name="gallery"
                          onChange={handleFileChange}
                          className="hidden"
                          id="gallery"
                          accept="image/*"
                          multiple
                        />
                        <label
                          htmlFor="gallery"
                          className="border-transparent inline-flex items-center rounded-md border bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                          Select Images
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 flex justify-end border-t border-gray-200 pt-6">
              <button
                type="submit"
                className="border-transparent inline-flex items-center rounded-md border bg-indigo-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Add Product
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateProductByVendor;
